import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { setVisible } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { XXL_COLORS } from '../../theme/colors';

const getEmailVisible = (state: any) => !!state.ui?.visibilityMap?.sendEmail;

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    paper: {
      borderRadius: 0,
      border: `2px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
    },
    title: {
      borderBottom: `1px solid ${tokens.borderDefault}`,
      paddingBottom: theme.spacing(1.5),
      '& h2': {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: tokens.sectionTitle,
      },
    },
    content: {
      padding: theme.spacing(2.5, 3),
      color: tokens.sectionTitle,
      fontSize: 14,
      lineHeight: 1.6,
    },
    actions: {
      borderTop: `1px solid ${tokens.borderDefault}`,
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
    },
    sendButton: {
      backgroundColor: tokens.accent,
      color: tokens.paperBackground,
      fontWeight: 700,
      borderRadius: 0,
      '&:hover': {
        backgroundColor: tokens.accent,
        opacity: 0.85,
      },
    },
  };
});

export default function SendEmailDialog() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(getEmailVisible);
  const code = useSelector((state: any) => state.configurator?.configuration?.code ?? null);
  const shareUrl = code ? `${getConf('network.host') || window.location.origin}/code:${code}` : '';

  const emailUrl = `mailto:?subject=${encodeURIComponent(t('share.email.subject'))}&body=${encodeURIComponent(t('share.email.body', { shareUrl }))}`;

  const handleClose = React.useCallback(() => {
    dispatch(setVisible('sendEmail', false));
  }, [dispatch]);

  const handleSend = React.useCallback(() => {
    window.open(emailUrl, '_blank');
    dispatch(setVisible('sendEmail', false));
  }, [emailUrl, dispatch]);

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ className: classes.paper }} maxWidth="xs" fullWidth>
      <DialogTitle className={classes.title} disableTypography={false}>
        {t('sendEmailDialog.title')}
      </DialogTitle>

      <DialogContent className={classes.content}>
        {t('sendEmailDialog.content')}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} size="small">
          {t('common.cancel') || 'Abbrechen'}
        </Button>
        <Button
          onClick={handleSend}
          className={classes.sendButton}
          variant="contained"
          size="small"
          disableElevation
        >
          {t('sendEmailDialog.send') || 'E-Mail senden →'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
